/**
 * Unified event tracking.
 *
 * Every CTA/conversion event flows through trackEvent():
 *   1) Fire-and-forget POST to /api/analytics  (persisted to AnalyticsEvent)
 *   2) dataLayer.push for GTM/GA4/pixel if loaded
 *
 * Before consent is granted, events are buffered in memory and flushed on accept.
 *
 * NOTE (taliora-west): Yandex Metrica channel removed during the western fork.
 * GA4/Plausible will plug in via the dataLayer bridge or a new channel here.
 */

import { UtmBundle } from "./utm";

export type EventProps = Record<string, string | number | boolean | null | undefined>;

interface BufferedEvent {
  event: string;
  props: EventProps;
  ts: number;
}

const buffer: BufferedEvent[] = [];
let consentGranted = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
    __tlr_session_id?: string;
  }
}

export function configureTracking(opts: { consent?: boolean }) {
  if (typeof opts.consent === "boolean") consentGranted = opts.consent;
  if (opts.consent) flushBuffer();
}

export function grantConsent() {
  consentGranted = true;
  flushBuffer();
}

export function revokeConsent() {
  consentGranted = false;
}

function flushBuffer() {
  while (buffer.length) {
    const e = buffer.shift();
    if (e) sendToChannels(e.event, e.props);
  }
}

function sendToChannels(event: string, props: EventProps) {
  // 1) Server-side persistence (AnalyticsEvent)
  if (typeof fetch !== "undefined") {
    fetch("/api/analytics", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        event,
        sessionId: getSessionId(),
        properties: cleanProps(props),
      }),
    }).catch(() => {});
  }

  // 2) GTM / GA4 / pixel bridge
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({ event, ...cleanProps(props) });
  }
}

export function trackEvent(event: string, props: EventProps = {}) {
  if (!consentGranted) {
    buffer.push({ event, props, ts: Date.now() });
    if (buffer.length > 50) buffer.shift();
    return;
  }
  sendToChannels(event, props);
}

export function trackPageView(pageId: string, utm: UtmBundle = {}, extra: EventProps = {}) {
  trackEvent("page_view", {
    pageId,
    path: typeof window !== "undefined" ? window.location.pathname : "",
    ...utm,
    ...extra,
  });
}

function cleanProps(props: EventProps): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

function getSessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  if (window.__tlr_session_id) return window.__tlr_session_id;
  const match = document.cookie.match(/(?:^|;\s*)tlr_sid=([^;]+)/);
  if (match) {
    window.__tlr_session_id = match[1];
    return match[1];
  }
  return undefined;
}
